import React, { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../config/api";

export default function GestionInformes() {
  const API = useMemo(() => API_BASE, []);

  // UI
  const [modoInforme, setModoInforme] = useState("sala"); // "colegio" | "sala" | "equipo"
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");

  // Filtros base
  const [colegios, setColegios] = useState([]);
  const [colegioId, setColegioId] = useState("");

  const [salas, setSalas] = useState([]);
  const [salaId, setSalaId] = useState("");

  // Equipos (para modo "equipo")
  const [equipos, setEquipos] = useState([]);
  const [equipoId, setEquipoId] = useState("");

  // Preview JSON
  const [previewSala, setPreviewSala] = useState(null);
  const [previewEquipo, setPreviewEquipo] = useState(null);

  // Informe integral por colegio (modo resumido/completo)
  const [tipoIntegral, setTipoIntegral] = useState(""); // "" | "resumido" | "completo"

  const incluirInactivos = true;

  // Helpers
  const limpiarTodo = () => {
    setError("");
    setSalas([]);
    setSalaId("");
    setEquipos([]);
    setEquipoId("");
    setPreviewSala(null);
    setPreviewEquipo(null);
    setTipoIntegral("");
  };

  const siNo = (v) => (Number(v) === 1 ? "Sí" : "No");
  const estadoActivo = (v) => (Number(v) === 1 ? "Activo" : "Inactivo");

  // Cargar colegios
  useEffect(() => {
    setCargando(true);
    fetch(`${API}/api/colegios`)
      .then((r) => r.json())
      .then((data) => setColegios(Array.isArray(data) ? data : []))
      .catch(() => setColegios([]))
      .finally(() => setCargando(false));
  }, [API]);

  // Cargar salas del colegio seleccionado
  useEffect(() => {
    if (!colegioId) {
      limpiarTodo();
      return;
    }

    setError("");
    setCargando(true);

    fetch(`${API}/api/colegios/${colegioId}/salas`)
      .then((r) => r.json())
      .then((data) => setSalas(Array.isArray(data) ? data : []))
      .catch(() => setSalas([]))
      .finally(() => setCargando(false));

    // reset dependencias
    setSalaId("");
    setEquipos([]);
    setEquipoId("");
    setPreviewSala(null);
    setPreviewEquipo(null);
  }, [API, colegioId]);

  // Cargar equipos al seleccionar sala (solo modo equipo)
  useEffect(() => {
    if (modoInforme !== "equipo") return;

    if (!salaId) {
      setEquipos([]);
      setEquipoId("");
      setPreviewEquipo(null);
      return;
    }

    setCargando(true);
    setError("");

    fetch(`${API}/api/salas/${salaId}/equipos`)
      .then((r) => r.json())
      .then((data) => setEquipos(Array.isArray(data) ? data : []))
      .catch(() => setEquipos([]))
      .finally(() => setCargando(false));

    setEquipoId("");
    setPreviewEquipo(null);
  }, [API, modoInforme, salaId]);

  // Preview por sala
  const cargarPreviewSala = async () => {
    if (!salaId) {
      setError("Seleccioná una sala para generar la vista previa.");
      return;
    }
    setError("");
    setPreviewSala(null);
    setCargando(true);

    try {
      const url = `${API}/api/informes/sala/${salaId}?incluir_inactivos=${incluirInactivos ? 1 : 0}`;
      const resp = await fetch(url);
      const json = await resp.json();

      if (!resp.ok) {
        setError(json?.mensaje || "No se pudo obtener el informe por sala.");
        return;
      }

      setPreviewSala(json);
    } catch {
      setError("Error de conexión al cargar el informe por sala.");
    } finally {
      setCargando(false);
    }
  };

  const exportarSalaExcel = () => {
    if (!salaId) return setError("Seleccioná una sala antes de exportar.");
    setError("");
    window.open(
      `${API}/api/exportar-informe-sala-excel/${salaId}?incluir_inactivos=${incluirInactivos ? 1 : 0}`,
      "_blank"
    );
  };

  const exportarSalaPDF = () => {
    if (!salaId) return setError("Seleccioná una sala antes de exportar.");
    setError("");
    window.open(
      `${API}/api/exportar-informe-sala-pdf/${salaId}?incluir_inactivos=${incluirInactivos ? 1 : 0}`,
      "_blank"
    );
  };

  // Preview por equipo
  const cargarPreviewEquipo = async () => {
    if (!equipoId) {
      setError("Seleccioná un equipo para generar la vista previa.");
      return;
    }
    setError("");
    setPreviewEquipo(null);
    setCargando(true);

    try {
      const url = `${API}/api/informes/equipo/${equipoId}?incluir_inactivos=${incluirInactivos ? 1 : 0}`;
      const resp = await fetch(url);
      const json = await resp.json();

      if (!resp.ok) {
        setError(json?.mensaje || "No se pudo obtener el informe por equipo.");
        return;
      }

      setPreviewEquipo(json);
    } catch {
      setError("Error de conexión al cargar el informe por equipo.");
    } finally {
      setCargando(false);
    }
  };

  const exportarEquipoExcel = () => {
    if (!equipoId) return setError("Seleccioná un equipo antes de exportar.");
    setError("");
    window.open(
      `${API}/api/exportar-informe-equipo-excel/${equipoId}?incluir_inactivos=${incluirInactivos ? 1 : 0}`,
      "_blank"
    );
  };

  const exportarEquipoPDF = () => {
    if (!equipoId) return setError("Seleccioná un equipo antes de exportar.");
    setError("");
    window.open(
      `${API}/api/exportar-informe-equipo-pdf/${equipoId}?incluir_inactivos=${incluirInactivos ? 1 : 0}`,
      "_blank"
    );
  };

  // Integral por colegio
  const exportarIntegralExcel = () => {
    if (!colegioId) return setError("Seleccioná un colegio antes de exportar.");
    if (!tipoIntegral) return setError("Seleccioná Informe Resumido o Informe Completo.");
    setError("");

    // OJO: tu excel.js hoy usa /api/exportar-informe-colegio-excel?colegio_id=...
    // Si querés mantener este componente, agregamos la ruta nueva en excel.js.
    window.open(
      `${API}/api/exportar-informe-integral-colegio-excel/${colegioId}?modo=${tipoIntegral}&incluir_inactivos=${incluirInactivos ? 1 : 0}`,
      "_blank"
    );
  };

  const exportarIntegralPDF = () => {
    if (!colegioId) return setError("Seleccioná un colegio antes de exportar.");
    if (!tipoIntegral) return setError("Seleccioná Informe Resumido o Informe Completo.");
    setError("");

    window.open(
      `${API}/api/exportar-informe-integral-colegio-pdf/${colegioId}?modo=${tipoIntegral}&incluir_inactivos=${incluirInactivos ? 1 : 0}`,
      "_blank"
    );
  };

  const Card = ({ title, subtitle, active, onClick }) => (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        textAlign: "left",
        padding: 16,
        borderRadius: 14,
        border: active ? "2px solid #2563eb" : "1px solid #e5e7eb",
        background: active ? "#eff6ff" : "white",
        boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
        cursor: "pointer",
      }}
      type="button"
    >
      <div style={{ fontSize: 16, fontWeight: 800, color: "#111827" }}>{title}</div>
      <div style={{ marginTop: 6, fontSize: 13, color: "#6b7280" }}>{subtitle}</div>
    </button>
  );

  return (
    <div className="p-6">
      <h2 className="admin-h2">Informes</h2>
      <p className="admin-p">
        Generá reportes por <b>Colegio</b>, por <b>Sala</b> o por <b>Equipo</b>.
      </p>

      <div className="admin-sep" />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <Card
          title="Informe Integral por Colegio"
          subtitle="Todas las salas + equipos por sala. (Resumido / Completo)"
          active={modoInforme === "colegio"}
          onClick={() => {
            setModoInforme("colegio");
            setPreviewSala(null);
            setPreviewEquipo(null);
            setError("");
            setEquipos([]);
            setEquipoId("");
            setSalaId("");
            setTipoIntegral("");
          }}
        />

        <Card
          title="Informe por Sala"
          subtitle="Detalle de sala + detalle de equipos de esa sala."
          active={modoInforme === "sala"}
          onClick={() => {
            setModoInforme("sala");
            setPreviewSala(null);
            setPreviewEquipo(null);
            setError("");
            setEquipos([]);
            setEquipoId("");
            setTipoIntegral("");
          }}
        />

        <Card
          title="Informe por Equipo"
          subtitle="Detalle completo de un equipo específico."
          active={modoInforme === "equipo"}
          onClick={() => {
            setModoInforme("equipo");
            setPreviewSala(null);
            setPreviewEquipo(null);
            setError("");
            setTipoIntegral("");
            setEquipoId("");
          }}
        />
      </div>

      <div className="bg-white shadow p-4 rounded mb-4 space-y-3">
        <div>
          <label className="block text-sm font-semibold text-gray-700">Colegio</label>
          <select
            value={colegioId}
            onChange={(e) => {
              setColegioId(e.target.value);
              setSalaId("");
              setEquipos([]);
              setEquipoId("");
              setPreviewSala(null);
              setPreviewEquipo(null);
              setTipoIntegral("");
              setError("");
            }}
            className="w-full border px-3 py-2 rounded"
          >
            <option value="">-- Seleccionar colegio --</option>
            {colegios.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </select>
        </div>

        {(modoInforme === "sala" || modoInforme === "equipo") && (
          <div>
            <label className="block text-sm font-semibold text-gray-700">Sala</label>
            <select
              value={salaId}
              onChange={(e) => {
                setSalaId(e.target.value);
                setEquipos([]);
                setEquipoId("");
                setPreviewSala(null);
                setPreviewEquipo(null);
                setError("");
              }}
              className="w-full border px-3 py-2 rounded"
              disabled={!colegioId}
            >
              <option value="">-- Seleccionar sala --</option>
              {salas.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre} ({s.tipo})
                </option>
              ))}
            </select>
          </div>
        )}

        {modoInforme === "equipo" && (
          <div>
            <label className="block text-sm font-semibold text-gray-700">Equipo</label>
            <select
              value={equipoId}
              onChange={(e) => {
                setEquipoId(e.target.value);
                setPreviewEquipo(null);
                setError("");
              }}
              className="w-full border px-3 py-2 rounded"
              disabled={!salaId}
            >
              <option value="">-- Seleccionar equipo --</option>
              {equipos.map((eq) => (
                <option key={eq.id} value={eq.id}>
                  {eq.nombre} ({Number(eq.activo) === 1 ? "Activo" : "Inactivo"})
                </option>
              ))}
            </select>
          </div>
        )}

        {modoInforme === "colegio" && (
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">Tipo de informe</label>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => {
                  setTipoIntegral("resumido");
                  setError("");
                }}
                className={`px-4 py-2 rounded ${tipoIntegral === "resumido" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
                disabled={!colegioId}
              >
                Informe Resumido
              </button>

              <button
                type="button"
                onClick={() => {
                  setTipoIntegral("completo");
                  setError("");
                }}
                className={`px-4 py-2 rounded ${tipoIntegral === "completo" ? "bg-blue-600 text-white" : "bg-gray-200"}`}
                disabled={!colegioId}
              >
                Informe Completo
              </button>
            </div>

            {tipoIntegral && (
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
                <button
                  type="button"
                  onClick={exportarIntegralExcel}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                  disabled={cargando}
                >
                  Exportar Excel
                </button>

                <button
                  type="button"
                  onClick={exportarIntegralPDF}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                  disabled={cargando}
                >
                  Exportar PDF
                </button>
              </div>
            )}
          </div>
        )}

        {modoInforme === "sala" && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={cargarPreviewSala}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              disabled={cargando}
            >
              Ver vista previa (Sala)
            </button>

            <button
              type="button"
              onClick={exportarSalaExcel}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              disabled={cargando}
            >
              Exportar Excel
            </button>

            <button
              type="button"
              onClick={exportarSalaPDF}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              disabled={cargando}
            >
              Exportar PDF
            </button>
          </div>
        )}

        {modoInforme === "equipo" && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={cargarPreviewEquipo}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              disabled={cargando}
            >
              Ver vista previa (Equipo)
            </button>

            <button
              type="button"
              onClick={exportarEquipoExcel}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              disabled={cargando}
            >
              Exportar Excel
            </button>

            <button
              type="button"
              onClick={exportarEquipoPDF}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              disabled={cargando}
            >
              Exportar PDF
            </button>
          </div>
        )}

        {cargando && <p className="text-gray-600">Cargando...</p>}
        {error && (
          <div className="p-3 rounded bg-red-50 text-red-700 border border-red-200">❌ {error}</div>
        )}
      </div>

      {/* Preview por Sala */}
      {modoInforme === "sala" && previewSala && (
        <div className="bg-white shadow p-4 rounded space-y-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Vista previa — Informe por Sala</h3>
            <p className="text-sm text-gray-600">Exportá a Excel/PDF con los botones.</p>
          </div>

          <div className="border rounded p-3">
            <div className="font-semibold text-gray-800">Colegio: {previewSala?.colegio?.nombre}</div>
            <div className="text-sm text-gray-600">
              Sala: {previewSala?.sala?.nombre} ({previewSala?.sala?.tipo}) · {estadoActivo(previewSala?.sala?.activo)}
            </div>
          </div>

          <div className="border rounded p-3">
            <div className="font-semibold text-gray-800 mb-2">Detalle de Sala</div>
            {!previewSala.detalle_sala ? (
              <div className="text-sm text-gray-600">No hay detalle registrado para esta sala.</div>
            ) : (
              <div className="text-sm text-gray-700 space-y-1">
                <div>
                  Impresora: {siNo(previewSala.detalle_sala.tiene_impresora)}{" "}
                  {previewSala.detalle_sala.impresora_marca
                    ? `(${previewSala.detalle_sala.impresora_marca} ${previewSala.detalle_sala.impresora_modelo || ""})`
                    : ""}
                </div>
                <div>Cable USB impresora: {siNo(previewSala.detalle_sala.impresora_tiene_cable_usb)}</div>

                <div>
                  Proyector: {siNo(previewSala.detalle_sala.tiene_proyector)}{" "}
                  {previewSala.detalle_sala.proyector_marca
                    ? `(${previewSala.detalle_sala.proyector_marca} ${previewSala.detalle_sala.proyector_modelo || ""})`
                    : ""}
                </div>
                <div>Cable proyector: {previewSala.detalle_sala.proyector_cable_tipo}</div>

                <div>
                  Televisor: {siNo(previewSala.detalle_sala.tiene_televisor)}{" "}
                  {previewSala.detalle_sala.televisor_marca
                    ? `(${previewSala.detalle_sala.televisor_marca} ${previewSala.detalle_sala.televisor_pulgadas || ""}" )`
                    : ""}
                </div>
                <div>Cable televisor: {previewSala.detalle_sala.televisor_cable_tipo}</div>
                <div>Control televisor: {siNo(previewSala.detalle_sala.televisor_tiene_control)}</div>

                {previewSala.detalle_sala.observacion && (
                  <div className="mt-2">
                    <div className="font-semibold">Observación:</div>
                    <div>{previewSala.detalle_sala.observacion}</div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="border rounded p-3">
            <div className="font-semibold text-gray-800 mb-2">Equipos ({previewSala.equipos?.length || 0})</div>

            {(previewSala.equipos || []).length === 0 ? (
              <div className="text-sm text-gray-600">No hay equipos en esta sala.</div>
            ) : (
              <div className="space-y-3">
                {previewSala.equipos.map((item, idx) => (
                  <div key={idx} className="border rounded p-3">
                    <div className="font-semibold text-gray-900">
                      {item.equipo.nombre} · {estadoActivo(item.equipo.activo)}
                    </div>

                    {!item.detalle_equipo ? (
                      <div className="text-sm text-gray-600">Sin detalle registrado para este equipo.</div>
                    ) : (
                      <div className="text-sm text-gray-700 mt-2 space-y-1">
                        <div>Placa madre: {item.detalle_equipo.placa_madre_modelo || "-"}</div>
                        <div>Procesador: {item.detalle_equipo.procesador_modelo || "-"}</div>
                        <div>RAM (GB): {item.detalle_equipo.ram_gb ?? "-"}</div>
                        <div>
                          Disco: {item.detalle_equipo.disco_tipo || "-"}{" "}
                          {item.detalle_equipo.disco_gb ? `(${item.detalle_equipo.disco_gb} GB)` : ""}
                        </div>
                        <div>Teclado: {siNo(item.detalle_equipo.tiene_teclado)}</div>
                        <div>Mouse: {siNo(item.detalle_equipo.tiene_mouse)}</div>
                        <div>Cable power: {siNo(item.detalle_equipo.tiene_cable_power)}</div>
                        <div>
                          Cable video: {siNo(item.detalle_equipo.tiene_cable_video)}{" "}
                          {Number(item.detalle_equipo.tiene_cable_video) === 1
                            ? `(${item.detalle_equipo.cable_video_tipo || "-"})`
                            : ""}
                        </div>
                        <div>
                          Monitor: {item.detalle_equipo.monitor_marca || "-"}{" "}
                          {item.detalle_equipo.monitor_pulgadas ? `(${item.detalle_equipo.monitor_pulgadas}")` : ""}
                        </div>

                        {item.detalle_equipo.observacion && (
                          <div className="mt-2">
                            <div className="font-semibold">Observación:</div>
                            <div>{item.detalle_equipo.observacion}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preview por Equipo */}
      {modoInforme === "equipo" && previewEquipo && (
        <div className="bg-white shadow p-4 rounded space-y-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Vista previa — Informe por Equipo</h3>
            <p className="text-sm text-gray-600">Exportá a Excel/PDF con los botones.</p>
          </div>

          <div className="border rounded p-3">
            <div className="font-semibold text-gray-800">Colegio: {previewEquipo?.colegio?.nombre}</div>
            <div className="text-sm text-gray-600">
              Sala: {previewEquipo?.sala?.nombre} ({previewEquipo?.sala?.tipo}) · {estadoActivo(previewEquipo?.sala?.activo)}
            </div>
            <div className="text-sm text-gray-600">
              Equipo: {previewEquipo?.equipo?.nombre} · {estadoActivo(previewEquipo?.equipo?.activo)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}